import { createTheme } from '@mui/material/styles';

const getAppTheme = (mode) => {
  const isLight = mode === 'light';
  return createTheme({
    palette: {
      mode: mode,
      primary: {
        main: '#6366f1', // Indigo matching global design
        light: '#818cf8',
        dark: '#4f46e5',
        contrastText: '#ffffff',
      },
      secondary: {
        main: '#06b6d4', // Cyan matching global design
        light: '#22d3ee',
        dark: '#0891b2',
        contrastText: '#ffffff',
      },
      info: {
        main: '#3b82f6',
        light: '#60a5fa',
        dark: '#1d4ed8',
        contrastText: '#ffffff',
      },
      error: {
        main: '#ef4444',
        light: '#f87171',
        dark: '#dc2626',
        contrastText: '#ffffff',
      },
      warning: {
        main: '#f59e0b',
        light: '#fbbf24',
        dark: '#d97706',
        contrastText: isLight ? '#ffffff' : '#000000',
      },
      success: {
        main: '#10b981',
        light: '#34d399',
        dark: '#059669',
        contrastText: '#ffffff',
      },
      background: {
        default: isLight ? '#f8fafc' : '#080d1a', // Deep slate body vs Slate light background
        paper: isLight ? '#ffffff' : '#0d1526',   // Card surface
      },
      text: {
        primary: isLight ? '#0f172a' : '#ffffff',     // High legibility pure white vs dark slate
        secondary: isLight ? '#475569' : '#cbd5e1',   // Slate-white subtext vs slate-gray
        disabled: isLight ? '#94a3b8' : '#64748b',
      },
      divider: isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
    },
    typography: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      h1: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 },
      h2: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 },
      h3: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 },
      h4: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 },
      h5: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 },
      h6: { fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 700 },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? '#ffffff' : '#0d1526',
            borderBottom: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
            backgroundImage: 'none',
            color: isLight ? '#0f172a' : '#ffffff',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: '8px',
            padding: '8px 20px',
            fontWeight: 700,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none', // Strip default overlay
            border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
            backgroundColor: isLight ? '#ffffff' : '#0d1526',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            backgroundColor: isLight ? 'rgba(0, 0, 0, 0.01)' : 'rgba(255, 255, 255, 0.02)',
            transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.14)',
              borderWidth: '1.5px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(99, 102, 241, 0.45)',
            },
            '&.Mui-focused': {
              backgroundColor: 'rgba(99, 102, 241, 0.05)',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#6366f1',
                borderWidth: '2px',
              },
              boxShadow: '0 0 16px rgba(99, 102, 241, 0.28)',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: isLight ? '#475569' : '#cbd5e1',
            fontWeight: 500,
            '&.Mui-focused': {
              color: '#818cf8',
            },
          },
        },
      },
    },
  });
};

const theme = getAppTheme('dark');

export { theme, getAppTheme };
